import { useQuery } from "@tanstack/react-query";
import { GoogleMap, Marker, Polyline, useLoadScript } from "@react-google-maps/api";
import { useMemo, useState } from "react";

import { getUnitPlayback, type PlaybackResponse } from "@/api/fleet";
import { ExportPlaybackButton } from "./ExportPlaybackButton";

interface Props {
  unitId: number;
  start: string;
  end: string;
}

const DARK_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
];

export function PlaybackViewer({ unitId, start, end }: Props) {
  const apiKey = (import.meta as ImportMeta).env?.VITE_GOOGLE_MAPS_KEY ?? "";
  const { isLoaded } = useLoadScript({ googleMapsApiKey: apiKey });

  const { data, isLoading, isError, error } = useQuery<PlaybackResponse>({
    queryKey: ["unit-playback", unitId, start, end],
    queryFn: () =>
      getUnitPlayback(unitId, { start, end, maxPoints: 1500 }),
  });

  const [cursor, setCursor] = useState(0);

  const points = data?.points ?? [];
  const events = data?.events ?? [];
  const polyline = useMemo(
    () => points.map((p) => ({ lat: p.latitude, lng: p.longitude })),
    [points]
  );
  const head = points[Math.min(cursor, points.length - 1)] ?? null;

  if (!apiKey) {
    return (
      <p className="text-muted-200 text-sm">
        Configura VITE_GOOGLE_MAPS_KEY para ver el playback.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="playback-viewer">
      <div className="flex flex-wrap items-baseline gap-3 text-xs text-muted-200">
        <span>
          {data ? `${data.totalPoints} puntos` : isLoading ? "Cargando…" : "—"}
          {data?.sampled && (
            <span className="ml-1">
              · muestreo 1:{data.sampleStride}
            </span>
          )}
        </span>
        <span>{events.length} evento(s)</span>
        <div className="ml-auto">
          <ExportPlaybackButton
            unitId={unitId}
            start={start}
            end={end}
            disabled={!data || data.totalPoints === 0}
          />
        </div>
      </div>

      {isError && (
        <p className="text-rose-400 text-sm">
          Error al cargar playback: {String(error)}
        </p>
      )}

      <div className="h-[450px] rounded-md overflow-hidden border border-border">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={head ? { lat: head.latitude, lng: head.longitude } : { lat: 19.43, lng: -99.13 }}
            zoom={12}
            options={{
              styles: DARK_STYLE,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {polyline.length > 1 && (
              <Polyline
                path={polyline}
                options={{
                  strokeColor: "#22D3EE",
                  strokeOpacity: 0.85,
                  strokeWeight: 3,
                }}
              />
            )}
            {head && (
              <Marker
                position={{ lat: head.latitude, lng: head.longitude }}
                icon={{
                  path: 0,
                  fillColor: "#22D3EE",
                  fillOpacity: 1,
                  strokeColor: "#0F1117",
                  strokeWeight: 2,
                  scale: 8,
                }}
              />
            )}
            {events.map((ev, idx) => {
              // Place each event marker at the nearest point.
              const at = new Date(ev.at).getTime();
              const placement = points.find(
                (p) => new Date(p.at).getTime() >= at
              ) ?? points[points.length - 1];
              if (!placement) return null;
              return (
                <Marker
                  key={`ev-${idx}`}
                  position={{
                    lat: placement.latitude,
                    lng: placement.longitude,
                  }}
                  icon={{
                    path: 0,
                    fillColor:
                      ev.tipo === "hit"
                        ? "#F43F5E"
                        : ev.tipo.startsWith("pausa")
                          ? "#F59E0B"
                          : "#A78BFA",
                    fillOpacity: 1,
                    strokeColor: "#0F1117",
                    strokeWeight: 1,
                    scale: 5,
                  }}
                  title={`${ev.tipo} · ${new Date(ev.at).toLocaleString()}`}
                />
              );
            })}
          </GoogleMap>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-200 text-sm">
            Cargando mapa…
          </div>
        )}
      </div>

      {points.length > 0 && (
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={points.length - 1}
            value={cursor}
            onChange={(e) => setCursor(Number(e.target.value))}
            className="flex-1"
            data-testid="playback-cursor"
          />
          <span className="text-xs text-muted-200 tabular-nums">
            {head ? new Date(head.at).toLocaleString() : "—"}
          </span>
        </div>
      )}
    </div>
  );
}
